import * as HeroIcons from '@heroicons/react/24/outline';
import { Button } from "../../../../components/ui/button"
import { DataTableColumnHeader } from "../../../../components/ui/data-table-column-header"
import { Badge } from "../../../../components/ui/badge";
import { formatLastLogin } from 'lib/utils';

const createStandardHeader = (title: string) => () => (
  <div className="text-xs font-medium text-gray-700">{title}</div>
);

export const createUsersColumns = (
  onDeleteUser: (user: any) => void,
  onEditUser: (user: any) => void,
  onResendOnboardingEmail?: (user: any) => void
) => [
  {
    accessorKey: "name",
    header: ({ column }: any) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    cell: ({ row }: any) => (
      <div className="font-medium text-xs">{row.getValue("name") || "Unknown User"}</div>
    ),
    enableSorting: true,
    enableHiding: false,
  },
  {
    accessorKey: "mobile_no",
    header: createStandardHeader("Mobile Number"),
    cell: ({ row }: any) => <div className="text-xs">{row.getValue("mobile_no") || "-"}</div>,
    enableSorting: false,
  },
  {
    accessorKey: "email",
    header: ({ column }: any) => (
      <DataTableColumnHeader column={column} title="Email" />
    ),
    cell: ({ row }: any) => <div className="text-xs">{row.getValue("email") || "-"}</div>,
    enableSorting: true,
  },
  {
    id: "customers",
    accessorFn: (row: any) =>
      row.customers?.map((c: any) => c.cust_name).join(", ") || "-",
    header: ({ column }: any) => (
      <DataTableColumnHeader column={column} title="Customers" />
    ),
    cell: ({ row }: any) => {
      const customers = row.original.customers;

      if (!customers || customers.length === 0) {
        return <div className="text-xs">-</div>;
      }

      const names = customers.map((c: any) => c.cust_name);
      const maxVisible = 2;

      const visibleNames = names.slice(0, maxVisible).join(", ");
      const hasMore = names.length > maxVisible;

      return (
        <div className="text-xs truncate max-w-[220px]" title={names.join(", ")}>
          {visibleNames}
          {hasMore && " ..."}
        </div>
      );
    },
    enableSorting: true,
  },
  {
    accessorKey: "user_type",
    header: ({ column }: any) => (
      <DataTableColumnHeader column={column} title="User Type" />
    ),
    cell: ({ row }: any) => (
      <Badge variant="outline" className="text-xs">
        {row.getValue("user_type") || "-"}
      </Badge>
    ),
    enableSorting: false,
  },
  {
    accessorKey: "role_name",
    header: createStandardHeader("Role"),
    cell: ({ row }: any) => {
      const roleName = row.getValue("role_name");
      return (
        <Badge variant="secondary" className="text-xs">
          {roleName || "No Role"}
        </Badge>
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: "onboarding_email_status",
    header: ({ column }: any) => (
      <DataTableColumnHeader column={column} title="Onboarding Email" />
    ),
    cell: ({ row }: any) => {
      const user = row.original;
      const status = user.onboarding_email_status;
      const sentAt = user.onboarding_email_sent_at;
      const errorMsg = user.onboarding_email_error;

      if (!status) {
        return <div className="text-xs text-gray-400">—</div>;
      }

      if (status === "queued") {
        return (
          <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-700 border-gray-300 font-normal">
            Queued
          </Badge>
        );
      }

      if (status === "sent") {
        const formattedDate = sentAt ? new Date(sentAt).toLocaleString() : "";
        return (
          <Badge
            variant="outline"
            className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300 font-medium cursor-help"
            title={formattedDate ? `Sent at ${formattedDate}` : "Sent"}
          >
            Sent
          </Badge>
        );
      }

      if (status === "failed") {
        return (
          <div className="flex items-center gap-1.5">
            <Badge
              variant="destructive"
              className="text-xs font-medium cursor-help"
              title={errorMsg || "Failed to send onboarding email"}
            >
              Failed
            </Badge>
            {onResendOnboardingEmail && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onResendOnboardingEmail(user)}
                className="h-6 px-2 text-[11px] font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200"
              >
                <HeroIcons.ArrowPathIcon className="h-3 w-3 mr-1" />
                Resend
              </Button>
            )}
          </div>
        );
      }

      return <div className="text-xs">{status}</div>;
    },
    enableSorting: true,
  },
  {
    accessorKey: "last_login_at",
    header: ({ column }: any) => (
      <DataTableColumnHeader column={column} title="Last Login" />
    ),
    cell: ({ row }: any) => (
      <div className="text-xs">
        {formatLastLogin(row.getValue("last_login_at"))}
      </div>
    ),
    enableSorting: true,
  },
  {
    id: "actions",
    header: createStandardHeader("Actions"),
    cell: ({ row }: any) => {
      const user = row.original;
      return (
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEditUser(user)}
            className="h-7 px-2.5 py-1 text-xs bg-blue-100 text-blue-600 border-blue-200 hover:bg-blue-200 hover:text-blue-700"
          >
            <HeroIcons.PencilSquareIcon className="h-4 w-4 mr-1" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDeleteUser(user)}
            className="h-7 px-2.5 py-1 text-xs bg-red-100 text-red-600 border-red-200 hover:bg-red-200 hover:text-red-700"
          >
            <HeroIcons.TrashIcon className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </div>
      );
    },
    enableSorting: false,
    enableHiding: false,
  },
]
